import { UserPreferences, preferencesApi } from './api/preferences';

const PREFERENCES_STORAGE_KEY = 'user_preferences';

/**
 * Get preferences from localStorage
 */
export function getLocalPreferences(): UserPreferences {
  if (typeof window === 'undefined') return {};

  try {
    const stored = localStorage.getItem(PREFERENCES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Failed to parse preferences from localStorage:', error);
    return {};
  }
}

/**
 * Save preferences to localStorage
 */
export function setLocalPreferences(preferences: UserPreferences): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.error('Failed to save preferences to localStorage:', error);
  }
}

/**
 * Clear preferences from localStorage
 */
export function clearLocalPreferences(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(PREFERENCES_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear preferences from localStorage:', error);
  }
}

/**
 * Fetch preferences from API and sync to localStorage
 */
export async function fetchAndSyncPreferences(): Promise<UserPreferences> {
  try {
    const response = await preferencesApi.get();
    const preferences = response.preferences || {};
    setLocalPreferences(preferences);
    return preferences;
  } catch (error) {
    console.error('Failed to fetch preferences from API:', error);
    return getLocalPreferences();
  }
}

/**
 * Update preferences on API and sync to localStorage
 */
export async function updatePreferences(updates: Partial<UserPreferences>): Promise<UserPreferences> {
  try {
    const response = await preferencesApi.update({ preferences: updates });
    const preferences = response.preferences || {};
    setLocalPreferences(preferences);
    return preferences;
  } catch (error) {
    console.error('Failed to update preferences:', error);
    throw error;
  }
}

/**
 * Get a specific preference value with a default fallback
 */
export function getPreference<T>(path: string[], defaultValue: T): T {
  const preferences = getLocalPreferences();

  let value: unknown = preferences;
  for (const key of path) {
    if (value && typeof value === 'object' && key in value) {
      value = (value as Record<string, unknown>)[key];
    } else {
      return defaultValue;
    }
  }

  return (value as T) ?? defaultValue;
}

/**
 * Set a specific preference value
 */
export async function setPreference(path: string[], value: unknown): Promise<void> {
  const preferences = getLocalPreferences();

  // Build the nested object
  const updates: Record<string, unknown> = {};
  let current: Record<string, unknown> = updates;

  for (let i = 0; i < path.length - 1; i++) {
    current[path[i]] = { ...(preferences[path[i]] as Record<string, unknown> || {}) };
    current = current[path[i]] as Record<string, unknown>;
  }

  current[path[path.length - 1]] = value;

  await updatePreferences(updates);
}
